import { Enums } from "../helper/enums";
import logoLight from "@/assets/logo-light.svg";
import logoDark from "@/assets/logo.svg";
export interface SignInThemeAppearance {
  topLeftColor: string;
  bottomRightColor: string;
  bgColor: string;
}

export interface SignInAppearance {
  dark: SignInThemeAppearance;
  light: SignInThemeAppearance;
}

export interface CxThemeSetting {
  textColor: string;
}

export interface CxThemeSettings {
  dark: CxThemeSetting;
  light: CxThemeSetting;
}

/** Portal theme ids: 1 = dark, 2 = light (matches preset-loader / useApp). */
export const CX_DARK_THEME_ID = 1;
export const CX_LIGHT_THEME_ID = 2;

export const DEFAULT_APP_LOGO =
  "https://app.gorelo.io/assets/logo-fe96e8a1.svg";
export const DEFAULT_LOGO_LIGHT = logoLight;
export const DEFAULT_LOGO_DARK = logoDark;

export const DEFAULT_SIGN_IN_THEME_APPEARANCE: SignInAppearance = {
  dark: {
    topLeftColor: "#8AEFDD",
    bottomRightColor: "#1DAF94",
    bgColor: "#1C1C1C",
  },
  light: {
    topLeftColor: "#FFFFFF",
    bottomRightColor: "#EEEEEE",
    bgColor: "#F5F5F5",
  },
};

export const DEFAULT_CX_THEME_SETTINGS: CxThemeSettings = {
  dark: {
    textColor: "#FFFFFF",
  },
  light: {
    textColor: "#1B1B1B",
  },
};

export const CX_APP_SETTINGS_DEFAULTS = {
  appTitle: "Customer Experience Application",
  brandColorLight: "#1DAF94",
  brandColorDark: "#8AEFDD",
  signInBackgroundType: Enums.SignInBackgroundType.Gradient,
  signInBackgroundTheme: Enums.SignInBackgroundTheme.Dark,
  enableContactInfo: false,
  organizationName: "",
  signInAppearance: DEFAULT_SIGN_IN_THEME_APPEARANCE,
  themeSettings: DEFAULT_CX_THEME_SETTINGS,
};

export function parseSignInBackgroundType(
  value: unknown,
): Enums.SignInBackgroundType {
  const parsed = Number(value);
  if (parsed === Enums.SignInBackgroundType.Solid) {
    return Enums.SignInBackgroundType.Solid;
  }
  return Enums.SignInBackgroundType.Gradient;
}

export function parseSignInBackgroundTheme(
  value: unknown,
): Enums.SignInBackgroundTheme {
  const parsed = Number(value);
  if (parsed === Enums.SignInBackgroundTheme.Light) {
    return Enums.SignInBackgroundTheme.Light;
  }
  if (parsed === Enums.SignInBackgroundTheme.System) {
    return Enums.SignInBackgroundTheme.System;
  }
  return Enums.SignInBackgroundTheme.Dark;
}

function parseSignInAppearanceValue(
  raw: unknown,
): Partial<SignInAppearance> | undefined {
  if (raw == null || raw === "") {
    return undefined;
  }
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Partial<SignInAppearance>;
    } catch {
      return undefined;
    }
  }
  return raw as Partial<SignInAppearance>;
}

function parseThemeSettingsValue(
  raw: unknown,
): Partial<CxThemeSettings> | undefined {
  if (raw == null || raw === "") {
    return undefined;
  }
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Partial<CxThemeSettings>;
    } catch {
      return undefined;
    }
  }
  return raw as Partial<CxThemeSettings>;
}

/** Resolves sign-in theme for logo/background (handles System via prefers-color-scheme). */
export function resolveSignInBackgroundTheme(
  settings: CxAppSettings,
): Enums.SignInBackgroundTheme {
  const theme =
    settings.signInBackgroundTheme ?? Enums.SignInBackgroundTheme.Dark;
  if (theme !== Enums.SignInBackgroundTheme.System) {
    return theme;
  }
  if (typeof window !== "undefined") {
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? Enums.SignInBackgroundTheme.Light
      : Enums.SignInBackgroundTheme.Dark;
  }
  return Enums.SignInBackgroundTheme.Dark;
}

function nonEmptyString(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  const trimmed = String(value).trim();
  return trimmed || undefined;
}

function mergeSignInThemeAppearance(
  raw: Partial<SignInThemeAppearance> | undefined,
  defaults: SignInThemeAppearance,
): SignInThemeAppearance {
  return {
    topLeftColor: nonEmptyString(raw?.topLeftColor) ?? defaults.topLeftColor,
    bottomRightColor:
      nonEmptyString(raw?.bottomRightColor) ?? defaults.bottomRightColor,
    bgColor: nonEmptyString(raw?.bgColor) ?? defaults.bgColor,
  };
}

export function mergeSignInAppearance(
  raw: Partial<SignInAppearance> | undefined,
): SignInAppearance {
  return {
    dark: mergeSignInThemeAppearance(
      raw?.dark,
      CX_APP_SETTINGS_DEFAULTS.signInAppearance.dark,
    ),
    light: mergeSignInThemeAppearance(
      raw?.light,
      CX_APP_SETTINGS_DEFAULTS.signInAppearance.light,
    ),
  };
}

function mergeThemeSetting(
  raw: Partial<CxThemeSetting> | undefined,
  defaults: CxThemeSetting,
): CxThemeSetting {
  return {
    textColor: nonEmptyString(raw?.textColor) ?? defaults.textColor,
  };
}

export function mergeThemeSettings(
  raw: Partial<CxThemeSettings> | undefined,
): CxThemeSettings {
  return {
    dark: mergeThemeSetting(
      raw?.dark,
      CX_APP_SETTINGS_DEFAULTS.themeSettings.dark,
    ),
    light: mergeThemeSetting(
      raw?.light,
      CX_APP_SETTINGS_DEFAULTS.themeSettings.light,
    ),
  };
}

export function applyCxAppSettingsDefaults(
  settings: CxAppSettings,
): CxAppSettings {
  settings.brandColorLight =
    nonEmptyString(settings.brandColorLight) ??
    CX_APP_SETTINGS_DEFAULTS.brandColorLight;
  settings.brandColorDark =
    nonEmptyString(settings.brandColorDark) ??
    CX_APP_SETTINGS_DEFAULTS.brandColorDark;
  settings.signInBackgroundType =
    settings.signInBackgroundType ??
    CX_APP_SETTINGS_DEFAULTS.signInBackgroundType;
  settings.signInBackgroundTheme =
    settings.signInBackgroundTheme ??
    CX_APP_SETTINGS_DEFAULTS.signInBackgroundTheme;
  settings.enableContactInfo =
    settings.enableContactInfo ?? CX_APP_SETTINGS_DEFAULTS.enableContactInfo;
  settings.showContactPreference = settings.enableContactInfo;
  settings.signInAppearance = mergeSignInAppearance(settings.signInAppearance);
  settings.themeSettings = mergeThemeSettings(settings.themeSettings);
  settings.organizationName =
    settings.organizationName ?? CX_APP_SETTINGS_DEFAULTS.organizationName;

  if (!nonEmptyString(settings.appTitle)) {
    settings.appTitle = CX_APP_SETTINGS_DEFAULTS.appTitle;
  }

  return settings;
}

export class CxAppSettings {
  public cxApplicationSettingsId: number;
  public serviceProviderId: number;
  public appTitle: string;
  public appLogoUrl: string;
  public appFavIconUrl: string;
  public showContactPreference: boolean;
  public serviceProviderName: string;
  public serviceProviderPhone: string;
  public customDomainUrl?: string;
  public logoLightUrl?: string;
  public logoDarkUrl?: string;
  public brandColorLight: string;
  public brandColorDark: string;
  public signInBackgroundType: Enums.SignInBackgroundType;
  public signInAppearance: SignInAppearance;
  public enableContactInfo: boolean;
  public organizationName: string;
  public supportEmail: string | null;
  public supportPhone: string | null;
  public signInBackgroundTheme: Enums.SignInBackgroundTheme;
  public themeSettings: CxThemeSettings;

  public constructor(
    __serviceProviderId: number,
    __appTitle: string,
    __appLogoUrl: string,
    __appFavIconUrl: string,
    __cxApplicationSettingsId: number,
    __showContactPreference: boolean = CX_APP_SETTINGS_DEFAULTS.enableContactInfo,
  ) {
    this.serviceProviderId = __serviceProviderId;
    this.appTitle = __appTitle;
    this.appLogoUrl = __appLogoUrl;
    this.appFavIconUrl = __appFavIconUrl;
    this.cxApplicationSettingsId = __cxApplicationSettingsId;
    this.showContactPreference = __showContactPreference;
    this.serviceProviderName = "";
    this.serviceProviderPhone = "";
    this.enableContactInfo = __showContactPreference;
    this.organizationName = CX_APP_SETTINGS_DEFAULTS.organizationName;
    this.supportEmail = null;
    this.supportPhone = null;
    this.brandColorLight = CX_APP_SETTINGS_DEFAULTS.brandColorLight;
    this.brandColorDark = CX_APP_SETTINGS_DEFAULTS.brandColorDark;
    this.signInBackgroundType = CX_APP_SETTINGS_DEFAULTS.signInBackgroundType;
    this.signInBackgroundTheme = CX_APP_SETTINGS_DEFAULTS.signInBackgroundTheme;
    this.signInAppearance = mergeSignInAppearance(
      CX_APP_SETTINGS_DEFAULTS.signInAppearance,
    );
    this.themeSettings = mergeThemeSettings(
      CX_APP_SETTINGS_DEFAULTS.themeSettings,
    );

    applyCxAppSettingsDefaults(this);
  }
}

export function normalizeCxAppSettings(raw: unknown): CxAppSettings | null {
  if (!raw || raw === "") {
    return null;
  }

  const data = raw as Record<string, unknown>;
  const settings = new CxAppSettings(
    Number(data.serviceProviderId ?? -1),
    String(data.appTitle ?? ""),
    String(data.appLogoUrl ?? ""),
    String(data.appFavIconUrl ?? ""),
    Number(data.cxApplicationSettingsId ?? -1),
    Boolean(
      data.enableContactInfo ??
        data.showContactPreference ??
        CX_APP_SETTINGS_DEFAULTS.enableContactInfo,
    ),
  );

  settings.serviceProviderName = String(data.serviceProviderName ?? "");
  settings.serviceProviderPhone = String(data.serviceProviderPhone ?? "");
  settings.customDomainUrl = nonEmptyString(data.customDomainUrl);
  settings.logoLightUrl = nonEmptyString(
    data.logoLightUrl || data.logoDarkUrl || DEFAULT_LOGO_LIGHT,
  );
  settings.logoDarkUrl = nonEmptyString(
    data.logoDarkUrl || data.logoLightUrl || DEFAULT_LOGO_DARK,
  );
  settings.brandColorLight =
    nonEmptyString(data.brandColorLight) ?? settings.brandColorLight;
  settings.brandColorDark =
    nonEmptyString(data.brandColorDark) ?? settings.brandColorDark;
  settings.signInBackgroundType =
    data.signInBackgroundType != null
      ? parseSignInBackgroundType(data.signInBackgroundType)
      : settings.signInBackgroundType;
  settings.signInAppearance = mergeSignInAppearance(
    parseSignInAppearanceValue(data.signInAppearance),
  );
  settings.themeSettings = mergeThemeSettings(
    parseThemeSettingsValue(data.themeSettings),
  );
  settings.enableContactInfo = Boolean(
    data.enableContactInfo ??
      data.showContactPreference ??
      CX_APP_SETTINGS_DEFAULTS.enableContactInfo,
  );
  settings.showContactPreference = settings.enableContactInfo;
  settings.organizationName = String(
    data.organizationName ?? CX_APP_SETTINGS_DEFAULTS.organizationName,
  );
  settings.supportEmail =
    data.supportEmail != null ? String(data.supportEmail) : null;
  settings.supportPhone =
    data.supportPhone != null ? String(data.supportPhone) : null;
  settings.signInBackgroundTheme =
    data.signInBackgroundTheme != null
      ? parseSignInBackgroundTheme(data.signInBackgroundTheme)
      : settings.signInBackgroundTheme;

  return applyCxAppSettingsDefaults(settings);
}

function pickThemedLogoUrl(settings: CxAppSettings, isLight: boolean): string {
  const themedLogo = isLight ? settings.logoLightUrl : settings.logoDarkUrl;

  if (themedLogo && themedLogo.trim() !== "") {
    return themedLogo;
  }

  if (settings.appLogoUrl && settings.appLogoUrl.trim() !== "") {
    return settings.appLogoUrl;
  }

  return "";
}

/** Logo for authenticated app shell (contact portal themeId). */
export function getCxAppLogoUrl(
  settings: CxAppSettings,
  themeId: number = CX_DARK_THEME_ID,
): string {
  return pickThemedLogoUrl(settings, themeId === CX_LIGHT_THEME_ID);
}

/** Logo for sign-in screen (signInBackgroundTheme from CX settings). */
export function getCxSignInLogoUrl(settings: CxAppSettings): string {
  const isLight =
    resolveSignInBackgroundTheme(settings) ===
    Enums.SignInBackgroundTheme.Light;
  return pickThemedLogoUrl(settings, isLight);
}

function appendBlobSasFromReference(
  blobUrl: string,
  referenceUrlWithSas: string,
): string {
  if (!blobUrl || blobUrl.includes("sig=")) {
    return blobUrl;
  }
  const sasIndex = referenceUrlWithSas.indexOf("?");
  if (sasIndex === -1) {
    return blobUrl;
  }
  const base = blobUrl.split("?")[0] ?? blobUrl;
  return base + referenceUrlWithSas.slice(sasIndex);
}

/** Reuse SAS from appLogoUrl when light/dark logo URLs are unsigned (login bootstrap). */
export function resolveAccessibleLogoUrl(
  themedLogoUrl: string,
  fallbackLogoUrl: string,
): string {
  if (themedLogoUrl && themedLogoUrl.trim() !== "") {
    if (themedLogoUrl.includes("sig=")) {
      return themedLogoUrl;
    }
    if (fallbackLogoUrl?.includes("sig=")) {
      return appendBlobSasFromReference(themedLogoUrl, fallbackLogoUrl);
    }
    return themedLogoUrl;
  }
  return fallbackLogoUrl?.trim() ?? "";
}
